import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../utils/axiosInstance";
import { formatStatus, getStatusBadgeClass } from "../utils/Utils";
import { useParams } from "react-router-dom";
import DownloadFileButton from "../components/DownloadFileButton";
import DownloadModFileButton from "../components/DownloadModFileButton";
import CancelFileButton from "../components/CancelFileButton";
import SupportChatBox from "./SupportChatBox";
import PaymentButton from "../components/PaymentButton";
import { FaTools } from "react-icons/fa";
import Notification from "../components/Notification";
import { formatToLocalTime } from "../utils/date";
import {
  Alert,
  Button,
  InfoTile,
  Page,
  PageHeader,
  PageLoader,
  Panel,
  SectionHeading,
} from "../components/ui";

/** A label/value row inside the detail panels. */
const DetailRow = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-2 last:border-0 dark:border-[#2c2c2e]">
    <span className="w-36 shrink-0 text-xs font-bold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">
      {label}
    </span>
    <span className="min-w-0 flex-1 break-words text-sm font-medium text-gray-800 dark:text-slate-200">
      {children}
    </span>
  </div>
);

const FileDetails = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [refreshDetails, setRefreshDetails] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    let intervalId;
    let previousData = null;

    const fetchFiles = async () => {
      try {
        const response = await axiosInstance.get(`/files/${id}`);
        const newData = response.data;

        if (JSON.stringify(newData) !== JSON.stringify(previousData)) {
          if (previousData !== null) {
            setShowNotification(true);
          }
          setData(newData);
          previousData = newData;
        }
      } catch (error) {
        console.error("Polling error", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
    intervalId = setInterval(fetchFiles, 3000);

    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Page>
        <PageHeader title="File Information" onBack={() => window.history.back()} />
        <PageLoader label="Loading file details…" />
      </Page>
    );
  }

  const { file, vehicle, ticket_id } = data;
  // An unpaid file the customer can't currently afford — worth flagging.
  const cannotAfford = file.price_status === "UNPAID" && user.credit_balance < file.price;

  return (
    <Page>
      <PageHeader
        title="File Information"
        subtitle={file.file_name}
        onBack={() => window.history.back()}
      >
        <span className={getStatusBadgeClass(file.status)}>{formatStatus(file.status)}</span>
        <span className={getStatusBadgeClass(file.price_status)}>
          {formatStatus(file.price_status)}
        </span>
      </PageHeader>

      <Alert tone="info">
        Your file will be reviewed by an admin. If there's an issue with the file, or the solution
        is unsupported, the process will be canceled. <strong>Notice:</strong> once the file is
        completed it cannot be canceled or refunded. For changes or questions, contact us via
        support for a quick response.
      </Alert>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel>
          <SectionHeading title="File Details" />
          <div className="mt-4">
            <DetailRow label="File ID">
              <span className="font-mono text-xs">{file.file_id}</span>
            </DetailRow>
            <DetailRow label="File Name">{file.file_name}</DetailRow>
            <DetailRow label="Solution Price">
              <span
                className={`font-bold ${
                  cannotAfford
                    ? "animate-pulse text-rose-600 dark:text-rose-400"
                    : "text-emerald-600 dark:text-emerald-400"
                }`}
              >
                CRS {file.price}
              </span>
            </DetailRow>
            <DetailRow label="Solution">{file.solution}</DetailRow>
            <DetailRow label="Description">{file.description}</DetailRow>
            <DetailRow label="Reading Device">{file.reading_device}</DetailRow>
            <DetailRow label="Status">
              <span className={getStatusBadgeClass(file.status)}>{formatStatus(file.status)}</span>
            </DetailRow>
            <DetailRow label="Price Status">
              <span className={getStatusBadgeClass(file.price_status)}>
                {formatStatus(file.price_status)}
              </span>
            </DetailRow>
            {file.feasible && (
              <DetailRow label="Feasible">
                <span className={getStatusBadgeClass(file.feasible)}>
                  {formatStatus(file.feasible)}
                </span>
              </DetailRow>
            )}
            <DetailRow label="Uploaded">{formatToLocalTime(file.created_at)}</DetailRow>
            <DetailRow label="Modified">{formatToLocalTime(file.updated_at)}</DetailRow>
          </div>
        </Panel>

        <Panel>
          <SectionHeading title="Files" />
          <div className="mt-4 space-y-3">
            <DownloadFileButton fileId={file.id} fileName={file.file_name} />

            <DownloadModFileButton
              fileId={file.id}
              fileName={file.mod_file_name}
              status={file.status}
              price_status={file.price_status}
            />

            {file.price_status === "UNPAID" &&
              file.status === "IN_PROGRESS" &&
              file.feasible !== "NOT_POSSIBLE" && (
                <div className="flex items-center gap-3 rounded-[14px] border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-700 dark:text-amber-300">
                  <FaTools className="h-5 w-5 shrink-0" />
                  <span>File is in progress, please wait…</span>
                </div>
              )}

            {file.feasible === "NOT_POSSIBLE" && (
              <Alert tone="error">
                At the moment this solution is NOT POSSIBLE — we will add it in the next update.
              </Alert>
            )}

            {file.price_status === "UNPAID" && file.feasible && file.feasible === "POSSIBLE" && (
              <PaymentButton file={file} setRefreshDetails={setRefreshDetails} />
            )}

            <CancelFileButton
              fileId={file.id}
              status={file.status}
              isAdmin={false}
              price_status={file.price_status}
              price={file.price}
            />

            {file.notes && (
              <Alert tone="warning">
                <span className="block font-bold uppercase tracking-[0.08em]">Important</span>
                {file.notes}
              </Alert>
            )}
          </div>
        </Panel>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel>
          <SectionHeading title="Vehicle Information" />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <InfoTile label="Type" value={vehicle.type} />
            <InfoTile label="Brand" value={vehicle.brand} accent />
            <InfoTile label="Model" value={vehicle.model} />
            <InfoTile label="ECU" value={vehicle.ecu} />
            <InfoTile label="Generation" value={vehicle.generation} />
            <InfoTile label="Gear" value={vehicle.gear} />
            <InfoTile label="Petrol Type" value={vehicle.petrol_type} />
          </div>
        </Panel>

        <Panel padded={!ticket_id}>
          {ticket_id ? (
            <SupportChatBox ticketId={ticket_id} role={user.role} />
          ) : (
            <>
              <SectionHeading title="Support" />
              {file && ["PROCESSED", "IN_PROGRESS", "PENDING"].includes(file.status) ? (
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    Something to ask about this file? Open a ticket and an admin will pick it up.
                  </p>
                  <Button to={`/create-ticket/${file.id}`}>Open Support Ticket</Button>
                </div>
              ) : (
                <p className="mt-4 text-sm text-gray-500 dark:text-slate-400">
                  Support tickets can be opened while a file is pending, in progress or processed.
                </p>
              )}
            </>
          )}
        </Panel>
      </div>

      {showNotification && (
        <Notification
          setShowNotification={setShowNotification}
          message={"File details have been updated."}
          isError={false}
        />
      )}
    </Page>
  );
};

export default FileDetails;
